// Pure planner for the "Invite past customers" backlog sweep (PR 1.3,
// config-calendar §3.3 / onboarding-funnel §2.5). The daily rotation only
// invites customers who ordered on the previous store-local calendar day
// (_shared/inviteWindow.ts); the backlog sweep works through everyone ELSE who
// is invitable — past customers whose latest order is older than that day — in
// batches of ~150 per weekday, filling the same day's invite campaign row. Import-free so it's unit-tested without a database
// (src/test/smsBacklogSweep.test.ts); the invite_backlog action applies it.

import { DEFAULT_BACKLOG_BATCH_SIZE } from "../_shared/inviteDefaults.ts";

const VALID_MOBILE = /^[2-9][0-9]{2}[2-9][0-9]{6}$/;

export { DEFAULT_BACKLOG_BATCH_SIZE };

export interface BacklogCandidate {
  customer_phone: string;
  contact_id: string | null;
  /** The view's own eligibility verdict — already consent/validity aware. */
  is_invitable: boolean;
  invite_count: number;
  last_order_at: string | null;
  /** Invites found in the outbound log for this phone (v_customer_invite_status). */
  history_invite_count?: number;
}

export interface BacklogRecipientPlan {
  customer_phone: string;
  contact_id: string | null;
  attempt_no: number;
  baseline_last_order_at: string | null;
}

export type BacklogBlockedReason =
  | "kill_switch"
  | "rotation_off"
  | "weekend"
  | "already_ran_today"
  | "daily_cap"
  | "monthly_cap"
  | "none_eligible";

export const BACKLOG_BLOCKED_MESSAGES: Record<BacklogBlockedReason, string> = {
  kill_switch: "Sending is paused by the emergency stop.",
  rotation_off: "Order Invite is off — turn it on before inviting past customers.",
  weekend: "Past-customer invites only go out on weekdays.",
  already_ran_today: "Past customers were already invited today — the next batch can go out next weekday.",
  daily_cap: "Today's send cap is reached — try again tomorrow.",
  monthly_cap: "This month's invite cap is reached.",
  none_eligible: "No past customers are eligible to invite right now.",
};

export interface BacklogSweepGateInput {
  killSwitch: boolean;
  rotationEnabled: boolean;
  /** Store-local day of week, 0 = Sunday … 6 = Saturday. */
  localWeekday: number;
  /** Whether a backlog sweep already ran on today's store-local date. */
  alreadyRanToday: boolean;
}

/** The cadence rules that refuse a sweep before any candidate is looked at:
 *  emergency stop, Order Invite off, weekends, and one sweep per weekday. */
export function backlogSweepGate(input: BacklogSweepGateInput): BacklogBlockedReason | null {
  if (input.killSwitch) return "kill_switch";
  if (!input.rotationEnabled) return "rotation_off";
  if (input.localWeekday === 0 || input.localWeekday === 6) return "weekend";
  if (input.alreadyRanToday) return "already_ran_today";
  return null;
}

/** Whether a marketing_campaigns audience_filter records a backlog sweep. */
export function isBacklogSweepMarked(audienceFilter: unknown): boolean {
  if (!audienceFilter || typeof audienceFilter !== "object") return false;
  return (audienceFilter as Record<string, unknown>).backlog_swept === true;
}

/** Whether invite_rotation has materialised today's batch into this row. A
 *  backlog sweep can open the row first (audience_filter.source = "backlog"),
 *  and the rotation reuses it at the send hour, stamping
 *  rotation_materialized_at; a row the rotation created itself never carries
 *  source "backlog", so it counts even without the stamp (rows from before it). */
export function isRotationMaterialized(audienceFilter: unknown): boolean {
  if (!audienceFilter || typeof audienceFilter !== "object") return true;
  const filter = audienceFilter as Record<string, unknown>;
  if (typeof filter.rotation_materialized_at === "string") return true;
  return filter.source !== "backlog";
}

/** Whether today's rotation failed for this row. A row the rotation created is
 *  marked status 'failed'; a row a backlog sweep opened keeps its status (its
 *  queued backlog invites still send and complete) and instead carries
 *  audience_filter.rotation_failed_at. */
export function isRotationFailed(status: string | null | undefined, audienceFilter: unknown): boolean {
  if (status === "failed") return true;
  if (!audienceFilter || typeof audienceFilter !== "object") return false;
  return typeof (audienceFilter as Record<string, unknown>).rotation_failed_at === "string";
}

export interface PlanBacklogSweepInput extends BacklogSweepGateInput {
  /** Invitable past customers (is_invitable rows) whose latest order predates
   *  yesterday, so today's batch window does not already cover them. */
  candidates: BacklogCandidate[];
  /** Phones already in today's invite campaign (the daily batch or an earlier sweep). */
  alreadyRecipientPhones: Set<string>;
  /** Phones that have ever been sent an invite — the backlog never re-invites them. */
  previouslyInvitedPhones: Set<string>;
  /** Phones the owner excluded from marketing texts — never invited. */
  excludedPhones: Set<string>;
  batchSize: number;
  /** Remaining daily/monthly invite budget, or null for no cap. */
  dailyCapRemaining: number | null;
  monthlyCapRemaining: number | null;
}

export interface BacklogSweepPlan {
  toQueue: BacklogRecipientPlan[];
  /** Present when nothing is queued: why. */
  blockedReason: BacklogBlockedReason | null;
}

/** Chooses up to `batchSize` past customers to invite this run, honouring the
 *  kill switch, the cadence gate and the daily/monthly caps, and skipping
 *  anyone already in the day's batch, ever sent an invite, or excluded from
 *  marketing texts by the owner. Each carries its
 *  real last_order_at as baseline so Proof can tell a backlog invite from a
 *  rolling one. */
export function planBacklogSweep(input: PlanBacklogSweepInput): BacklogSweepPlan {
  const { candidates, alreadyRecipientPhones, previouslyInvitedPhones, excludedPhones, batchSize } = input;

  const gate = backlogSweepGate(input);
  if (gate) return { toQueue: [], blockedReason: gate };

  let budget = batchSize;
  if (input.dailyCapRemaining !== null) budget = Math.min(budget, input.dailyCapRemaining);
  if (input.monthlyCapRemaining !== null) budget = Math.min(budget, input.monthlyCapRemaining);

  if (budget <= 0) {
    const reason = input.dailyCapRemaining !== null && input.dailyCapRemaining <= 0 ? "daily_cap" : "monthly_cap";
    return { toQueue: [], blockedReason: reason };
  }

  const toQueue: BacklogRecipientPlan[] = [];
  for (const c of candidates) {
    if (toQueue.length >= budget) break;
    if (!c.is_invitable) continue;
    if (!VALID_MOBILE.test(c.customer_phone)) continue;
    if (alreadyRecipientPhones.has(c.customer_phone)) continue;
    if (previouslyInvitedPhones.has(c.customer_phone)) continue;
    if (excludedPhones.has(c.customer_phone)) continue;
    if ((c.history_invite_count ?? 0) > 0) continue;
    toQueue.push({
      customer_phone: c.customer_phone,
      contact_id: c.contact_id,
      attempt_no: c.invite_count + 1,
      baseline_last_order_at: c.last_order_at,
    });
  }

  return { toQueue, blockedReason: toQueue.length === 0 ? "none_eligible" : null };
}
